import { formatear, type Moneda } from "@tora/shared";
import { cn } from "@/lib/utils";

export type TamanoImporte = "sm" | "md" | "lg" | "xl";

// Cuatro tamaños con rol: sm auxiliar, md celda/cuerpo, lg KPI secundario y
// titulo de ficha, xl KPI principal. `text-base` no es un rol de plata.
const TAMANOS: Record<TamanoImporte, string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-lg",
  xl: "text-2xl",
};

/**
 * Chip con el codigo de moneda. Es el mecanismo NO cromatico que hace que un
 * importe sea inequivoco: en blanco y negro, impreso o para alguien daltonico,
 * el texto "ARS" / "USD" sigue estando.
 */
export function ChipMoneda({
  moneda,
  className,
}: {
  moneda: Moneda;
  className?: string;
}): JSX.Element {
  return (
    <span
      data-moneda={moneda}
      className={cn(
        "select-none rounded-sm px-1 py-px text-2xs font-semibold uppercase leading-none tracking-wider",
        moneda === "ARS" ? "bg-ars-muted text-ars" : "bg-usd-muted text-usd",
        className,
      )}
    >
      {moneda}
    </span>
  );
}

export interface ImporteProps {
  monto: number;
  moneda: Moneda;
  /**
   * Muestra el chip ARS/USD. Default `true`.
   * Apagarlo SOLO si el encabezado de la columna ya declara la moneda de forma
   * inequivoca (ver MASTER.md seccion 3.4).
   */
  codigo?: boolean;
  tamano?: TamanoImporte;
  /** Antepone "+" a los positivos. Para libros mayores y movimientos de caja. */
  signoExplicito?: boolean;
  className?: string;
}

/**
 * Un importe de plata. La unica forma valida de mostrar dinero en TORA.
 *
 * Tres capas redundantes evitan confundir ARS con USD de un vistazo:
 *  1. El simbolo, que ya difiere: "$ 1.234,56" vs "US$ 1.234,56" (via `formatear()`
 *     de `@tora/shared`, que usa Intl es-AR).
 *  2. El chip de codigo "ARS" / "USD".
 *  3. El color semantico (`--ars` azul / `--usd` terracota), verificado para las
 *     tres dicromacias.
 *
 * Ninguna de las tres es la unica: si se cae el color, quedan dos.
 */
export function Importe({
  monto,
  moneda,
  codigo = true,
  tamano = "md",
  signoExplicito = false,
  className,
}: ImporteProps): JSX.Element {
  const texto = formatear(monto, moneda);
  const conSigno = signoExplicito && monto > 0 ? `+${texto}` : texto;

  return (
    <span
      data-moneda={moneda}
      data-monto={monto}
      className={cn(
        "inline-flex items-baseline gap-1.5 whitespace-nowrap font-medium tabular-nums",
        moneda === "ARS" ? "text-ars" : "text-usd",
        TAMANOS[tamano],
        className,
      )}
    >
      {codigo ? <ChipMoneda moneda={moneda} /> : null}
      <span>{conSigno}</span>
    </span>
  );
}
